import type {
  NovaClarificationAnswer,
  NovaClarificationOption,
  NovaClarificationQuestionDependency,
  NovaClarificationToolUIPart,
} from '@newfront-insurance/ai-api';
import type { UIDataTypes, UIMessagePart, UITools } from 'ai';

import type { UIMessageMetadata } from './types';

export type ClarificationInputType = 'text' | 'select' | 'multi-select' | 'file';

export type ClarificationOption = NovaClarificationOption;
export interface ClarificationQuestion {
  questionId: string;
  header: string;
  question: string;
  helperText?: string;
  dependsOn?: NovaClarificationQuestionDependency;
  inputType: ClarificationInputType;
  options?: ClarificationOption[];
  acceptedFileTypes?: string[];
  maxFiles?: number;
}

/** Includes file answers: sandbox `paths`, or Files `fileUuids` on answers stored before uploads. */
export type ClarificationAnswer = NovaClarificationAnswer;
export interface ClarificationInput {
  questions: ClarificationQuestion[];
}

interface ClarificationOptionLike {
  optionId?: string;
  label?: string;
  description?: string;
}

interface ClarificationQuestionLike {
  questionId?: string;
  header?: string;
  question?: string;
  helperText?: string;
  inputType?: unknown;
  options?: ClarificationOptionLike[];
  acceptedFileTypes?: unknown;
  maxFiles?: unknown;
  dependsOn?: unknown;
}

export interface ClarificationMetadata {
  state: 'awaiting-clarification' | 'clarification-resolved';
  activeToolCallId: string;
  rawInput?: unknown;
  resolvedTranscript?: string;
}

export type ClarificationToolPart = NovaClarificationToolUIPart;

export interface ClarificationAwareMessage {
  role: 'system' | 'user' | 'assistant';
  metadata?: Partial<UIMessageMetadata> & {
    clarification?: ClarificationMetadata;
  };
  parts: UIMessagePart<UIDataTypes, UITools>[];
}

export function isClarificationToolPart(part: UIMessagePart<UIDataTypes, UITools>): part is ClarificationToolPart {
  return part.type === 'tool-clarification';
}

const RECOMMENDED_LABEL_PATTERN = /\(recommended\)/i;

export function getRecommendedOptionId(question: ClarificationQuestion): string | undefined {
  if (question.inputType !== 'select' && question.inputType !== 'multi-select') {
    return undefined;
  }

  const firstOption = question.options?.[0];
  if (!firstOption || !RECOMMENDED_LABEL_PATTERN.test(firstOption.label)) {
    return undefined;
  }

  return firstOption.optionId;
}

export function getClarificationPart(message?: ClarificationAwareMessage | null): ClarificationToolPart | undefined {
  return message?.parts.slice().reverse().find(isClarificationToolPart);
}

export function isClarificationPending(message?: ClarificationAwareMessage | null): boolean {
  const clarificationPart = getClarificationPart(message);

  if (clarificationPart?.state === 'input-available') {
    return true;
  }

  if (clarificationPart?.state === 'output-available') {
    return false;
  }

  return message?.metadata?.clarification?.state === 'awaiting-clarification';
}

export function getLatestPendingClarification<M extends ClarificationAwareMessage>(
  messages: readonly M[],
): { message: M; part: ClarificationToolPart } | undefined {
  const lastMessage = messages.at(-1);

  if (lastMessage?.role !== 'assistant') {
    return undefined;
  }

  const part = getClarificationPart(lastMessage);
  if (part?.state !== 'input-available') {
    return undefined;
  }

  return { message: lastMessage, part };
}

export function getResolvedClarificationTranscript(
  message?: ClarificationAwareMessage | null,
  part?: ClarificationToolPart | null,
): string | undefined {
  const latestClarificationPart = getClarificationPart(message);
  const metadataTranscript =
    message?.metadata?.clarification?.state === 'clarification-resolved'
      ? message.metadata.clarification.resolvedTranscript
      : undefined;

  if (metadataTranscript && (!part || part.toolCallId === latestClarificationPart?.toolCallId)) {
    return metadataTranscript;
  }

  const clarificationPart = part ?? latestClarificationPart;
  if (clarificationPart?.state !== 'output-available') {
    return undefined;
  }

  const outputTranscript = getClarificationTranscriptFromOutput(clarificationPart.output);
  if (outputTranscript) {
    return outputTranscript;
  }

  const questions = normalizeClarificationInput(clarificationPart.input).questions;
  const answers = normalizeClarificationOutputAnswers(clarificationPart.output);

  if (questions.length === 0 || answers.length === 0) {
    return undefined;
  }

  return buildClarificationTranscript(questions, answers);
}

export function normalizeClarificationInput(input: unknown): ClarificationInput {
  if (!isObject(input) || !Array.isArray(input.questions)) {
    return { questions: [] };
  }

  const questions: ClarificationQuestion[] = [];
  const questionsByRawIndex: Array<ClarificationQuestion | undefined> = [];

  input.questions.forEach((question, questionIndex) => {
    const normalizedQuestion = normalizeClarificationQuestion(question, questionIndex, questionsByRawIndex);
    questionsByRawIndex[questionIndex] = normalizedQuestion ?? undefined;

    if (normalizedQuestion) {
      questions.push(normalizedQuestion);
    }
  });

  return { questions };
}

export function buildClarificationTranscript(
  questions: ClarificationQuestion[],
  answers: ClarificationAnswer[],
): string {
  return getActiveClarificationQuestions(questions, answers)
    .map((question) => {
      const answer = answers.find((candidate) => candidate.questionId === question.questionId);
      const resolvedAnswer = answer ? getClarificationAnswerText(question, answer) : 'Skipped';

      return `Q: ${question.question}\nA: ${resolvedAnswer}`;
    })
    .join('\n\n');
}

export function getActiveClarificationQuestions(
  questions: ClarificationQuestion[],
  answers: ClarificationAnswer[],
): ClarificationQuestion[] {
  const answersByQuestionId = new Map(answers.map((answer) => [answer.questionId, answer]));
  const activeQuestionIds = new Set<string>();
  const activeQuestions: ClarificationQuestion[] = [];

  for (const question of questions) {
    if (!isClarificationQuestionActive(question, answersByQuestionId, activeQuestionIds)) {
      continue;
    }

    activeQuestionIds.add(question.questionId);
    activeQuestions.push(question);
  }

  return activeQuestions;
}

export function getClarificationAnswerText(question: ClarificationQuestion, answer: ClarificationAnswer): string {
  if (isSkippedClarificationAnswer(answer)) {
    return 'Skipped';
  }

  if (isFileClarificationAnswer(answer)) {
    const count = answerFiles(answer).length;
    const summary = `Uploaded ${count} file${count === 1 ? '' : 's'}`;
    const additionalText = answer.text?.trim();
    return additionalText ? `${summary}. Additional details: ${additionalText}` : summary;
  }

  if (isMultiSelectClarificationAnswer(answer)) {
    const selectedOptions = answer.optionIds
      .map((optionId) => getClarificationOptionLabel(question, optionId))
      .join(', ');
    const additionalText = getClarificationAdditionalText(answer);

    if (selectedOptions && additionalText) {
      return `${selectedOptions}. Additional details: ${additionalText}`;
    }

    return selectedOptions || additionalText || 'Skipped';
  }

  if (isSingleSelectClarificationAnswer(answer)) {
    return getClarificationOptionLabel(question, answer.optionId);
  }

  if (isTextClarificationAnswer(answer)) {
    return answer.text.trim();
  }

  return 'Skipped';
}

function normalizeClarificationQuestion(
  question: unknown,
  questionIndex: number,
  questionsByRawIndex: Array<ClarificationQuestion | undefined>,
): ClarificationQuestion | null {
  if (!isObject(question)) {
    return null;
  }

  const typedQuestion = question as ClarificationQuestionLike;
  const questionText = typedQuestion.question?.trim();
  const questionId = typedQuestion.questionId?.trim() || `q${questionIndex + 1}`;
  const header = typedQuestion.header?.trim() || `Question ${questionIndex + 1}`;

  if (!questionText) {
    return null;
  }

  const normalizedOptions = typedQuestion.options
    ?.map((option, optionIndex) => normalizeClarificationOption(option, questionIndex, optionIndex))
    .filter((option): option is ClarificationOption => !!option);

  const inputType = normalizeClarificationInputType(typedQuestion);
  if (!inputType) {
    return null;
  }

  const supportsOptions = inputType === 'select' || inputType === 'multi-select';
  if (supportsOptions && !normalizedOptions?.length) {
    return null;
  }
  const dependsOn =
    typedQuestion.dependsOn === undefined
      ? undefined
      : normalizeClarificationDependency(typedQuestion.dependsOn, questionsByRawIndex);
  if (typedQuestion.dependsOn !== undefined && !dependsOn) {
    return null;
  }

  if (inputType === 'file') {
    return {
      questionId,
      header,
      question: questionText,
      helperText: typedQuestion.helperText?.trim() || undefined,
      ...(dependsOn ? { dependsOn } : {}),
      inputType,
      acceptedFileTypes: normalizeAcceptedFileTypes(typedQuestion.acceptedFileTypes),
      maxFiles: normalizeMaxFiles(typedQuestion.maxFiles),
    };
  }

  return {
    questionId,
    header,
    question: questionText,
    helperText: typedQuestion.helperText?.trim() || undefined,
    ...(dependsOn ? { dependsOn } : {}),
    inputType,
    options: supportsOptions ? normalizedOptions : undefined,
  };
}

function normalizeClarificationDependency(
  value: unknown,
  questionsByRawIndex: Array<ClarificationQuestion | undefined>,
): NovaClarificationQuestionDependency | undefined {
  if (!isObject(value)) {
    return undefined;
  }

  if (typeof value.questionId === 'string') {
    return normalizeClarificationIdDependency(value);
  }

  if (typeof value.questionIndex === 'number' && Number.isInteger(value.questionIndex)) {
    return normalizeClarificationIndexDependency(value, questionsByRawIndex);
  }

  return undefined;
}

function normalizeClarificationIdDependency(
  value: Record<string, unknown>,
): NovaClarificationQuestionDependency | undefined {
  const questionId = typeof value.questionId === 'string' ? value.questionId.trim() : '';
  if (!questionId) {
    return undefined;
  }

  if (typeof value.optionId === 'string' && value.optionId.trim()) {
    return {
      questionId,
      optionId: value.optionId.trim(),
    };
  }

  if (value.skipped === true) {
    return {
      questionId,
      skipped: true,
    };
  }

  return undefined;
}

function normalizeClarificationIndexDependency(
  value: Record<string, unknown>,
  questionsByRawIndex: Array<ClarificationQuestion | undefined>,
): NovaClarificationQuestionDependency | undefined {
  const questionIndex = value.questionIndex;
  if (typeof questionIndex !== 'number' || !Number.isInteger(questionIndex) || questionIndex < 0) {
    return undefined;
  }

  const parentQuestion = questionsByRawIndex[questionIndex];
  if (!parentQuestion) {
    return undefined;
  }

  if (value.skipped === true) {
    return {
      questionId: parentQuestion.questionId,
      skipped: true,
    };
  }

  const optionIndex = value.optionIndex;
  if (typeof optionIndex !== 'number' || !Number.isInteger(optionIndex) || optionIndex < 0) {
    return undefined;
  }

  const optionId = parentQuestion.options?.[optionIndex]?.optionId;
  if (!optionId) {
    return undefined;
  }

  return {
    questionId: parentQuestion.questionId,
    optionId,
  };
}

function normalizeClarificationInputType(question: ClarificationQuestionLike): ClarificationInputType | null {
  if (
    question.inputType === 'text' ||
    question.inputType === 'select' ||
    question.inputType === 'multi-select' ||
    question.inputType === 'file'
  ) {
    return question.inputType;
  }

  return null;
}

function normalizeAcceptedFileTypes(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const cleaned = value
    .filter((entry): entry is string => typeof entry === 'string')
    .map((entry) => entry.trim().replace(/^\./, '').toLowerCase())
    .filter((entry) => entry.length > 0);

  return cleaned.length > 0 ? cleaned : undefined;
}

function normalizeMaxFiles(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 1) {
    return undefined;
  }
  return Math.min(5, Math.floor(value));
}

function normalizeClarificationOption(
  option: ClarificationOptionLike | undefined,
  questionIndex: number,
  optionIndex: number,
): ClarificationOption | null {
  const optionId = option?.optionId?.trim() || `q${questionIndex + 1}_o${optionIndex + 1}`;
  const label = option?.label?.trim();

  if (!option || !label) {
    return null;
  }

  return {
    optionId,
    label,
    description: option.description?.trim() || undefined,
  };
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getClarificationTranscriptFromOutput(output: unknown): string | undefined {
  if (!isObject(output)) {
    return undefined;
  }

  const transcript = output.transcript;
  return typeof transcript === 'string' && transcript.trim().length > 0 ? transcript.trim() : undefined;
}

function normalizeClarificationOutputAnswers(output: unknown): ClarificationAnswer[] {
  if (!isObject(output) || !Array.isArray(output.answers)) {
    return [];
  }

  return output.answers
    .map((answer) => normalizeClarificationAnswer(answer))
    .filter((answer): answer is ClarificationAnswer => !!answer);
}

function normalizeClarificationAnswer(answer: unknown): ClarificationAnswer | null {
  if (!isObject(answer) || typeof answer.questionId !== 'string') {
    return null;
  }

  const questionId = answer.questionId.trim();

  if (!questionId) {
    return null;
  }

  if (answer.skipped === true) {
    return {
      questionId,
      skipped: true,
    };
  }
  const text = typeof answer.text === 'string' && answer.text.trim() ? answer.text.trim() : undefined;

  const files = (key: 'paths' | 'fileUuids'): string[] =>
    Array.isArray(answer[key])
      ? answer[key]
          .filter((file): file is string => typeof file === 'string')
          .map((file) => file.trim())
          .filter(Boolean)
      : [];
  const paths = files('paths');
  if (paths.length > 0) {
    return { questionId, paths, ...(text ? { text } : {}) };
  }
  const fileUuids = files('fileUuids');
  if (fileUuids.length > 0) {
    return { questionId, fileUuids, ...(text ? { text } : {}) };
  }

  if (Array.isArray(answer.optionIds)) {
    const optionIds = answer.optionIds
      .filter((optionId): optionId is string => typeof optionId === 'string')
      .map((optionId) => optionId.trim())
      .filter(Boolean);

    if (optionIds.length > 0) {
      return {
        questionId,
        optionIds,
        text,
      };
    }
  }

  if (typeof answer.optionId === 'string' && answer.optionId.trim()) {
    return {
      questionId,
      optionId: answer.optionId.trim(),
    };
  }

  if (text) {
    return {
      questionId,
      text,
    };
  }

  return null;
}

function getClarificationOptionLabel(question: ClarificationQuestion, optionId: string): string {
  return question.options?.find((option: ClarificationOption) => option.optionId === optionId)?.label ?? optionId;
}

function isClarificationQuestionActive(
  question: ClarificationQuestion,
  answersByQuestionId: Map<string, ClarificationAnswer>,
  activeQuestionIds: Set<string>,
): boolean {
  if (!question.dependsOn) {
    return true;
  }

  const parentAnswer = answersByQuestionId.get(question.dependsOn.questionId);
  if (!parentAnswer || !activeQuestionIds.has(question.dependsOn.questionId)) {
    return false;
  }

  if ('skipped' in question.dependsOn) {
    return isSkippedClarificationAnswer(parentAnswer);
  }

  if (isSingleSelectClarificationAnswer(parentAnswer)) {
    return parentAnswer.optionId === question.dependsOn.optionId;
  }

  if (isMultiSelectClarificationAnswer(parentAnswer)) {
    return parentAnswer.optionIds.includes(question.dependsOn.optionId);
  }

  return false;
}

function getClarificationAdditionalText(
  answer: Extract<ClarificationAnswer, { optionIds: string[] }>,
): string | undefined {
  return typeof answer.text === 'string' && answer.text.trim() ? answer.text.trim() : undefined;
}

function isSkippedClarificationAnswer(
  answer: ClarificationAnswer,
): answer is Extract<ClarificationAnswer, { skipped: true }> {
  return 'skipped' in answer && answer.skipped === true;
}

function isTextClarificationAnswer(
  answer: ClarificationAnswer,
): answer is Extract<ClarificationAnswer, { text: string }> {
  return (
    'text' in answer &&
    typeof answer.text === 'string' &&
    !('optionIds' in answer) &&
    !('paths' in answer) &&
    !('fileUuids' in answer)
  );
}

function isFileClarificationAnswer(
  answer: ClarificationAnswer,
): answer is Extract<ClarificationAnswer, { paths: string[] } | { fileUuids: string[] }> {
  return (
    ('paths' in answer && Array.isArray(answer.paths)) || ('fileUuids' in answer && Array.isArray(answer.fileUuids))
  );
}

function answerFiles(answer: Extract<ClarificationAnswer, { paths: string[] } | { fileUuids: string[] }>): string[] {
  return 'paths' in answer ? answer.paths : answer.fileUuids;
}

function isMultiSelectClarificationAnswer(
  answer: ClarificationAnswer,
): answer is Extract<ClarificationAnswer, { optionIds: string[] }> {
  return 'optionIds' in answer && Array.isArray(answer.optionIds);
}

function isSingleSelectClarificationAnswer(
  answer: ClarificationAnswer,
): answer is Extract<ClarificationAnswer, { optionId: string }> {
  return 'optionId' in answer && typeof answer.optionId === 'string';
}
